import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Shield, Users, Heart, Sliders } from "lucide-react";

const WhyChooseSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const reasons = [
    { 
      icon: Shield, 
      title: "Trusted Local Expert",
      description: "Years of experience serving the Crested Butte community with dedication."
    },
    { 
      icon: Users, 
      title: "Personalized & Attentive",
      description: "Discreet service tailored to your specific needs and preferences."
    },
    { 
      icon: Heart, 
      title: "Peace of Mind",
      description: "Complete care for absentee and seasonal homeowners, wherever you are."
    },
    { 
      icon: Sliders, 
      title: "Flexible Packages",
      description: "Customizable services with premium add-ons to fit your lifestyle."
    }
  ];

  return (
    <section
      ref={sectionRef}
      className="py-24 md:py-32 bg-gray-50"
      data-testid="why-choose-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="text-accent text-sm font-semibold tracking-wider uppercase">
              Why Choose Matthew
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-navy mt-4 mb-8 leading-tight">
              Experience the <span className="italic">Difference</span>
            </h2>

            {/* Reasons Grid */}
            <div className="grid sm:grid-cols-2 gap-6">
              {reasons.map((reason, index) => (
                <motion.div
                  key={reason.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                  className="bg-white p-5 rounded-xl shadow-sm"
                >
                  <reason.icon className="w-6 h-6 text-navy mb-3" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg text-navy mb-2">{reason.title}</h3>
                  <p className="text-gray-600 text-sm">{reason.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://i.pinimg.com/736x/ab/3e/32/ab3e32700086b67c1cfced94cb1f0b91.jpg"
                alt="Luxury Mountain Property"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;